import { CommonModule } from '@angular/common';
import { Component, Injector, input, Input, output, ViewChild } from '@angular/core';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GetContextMenuItemsParams,
  ICellRendererParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { EMPTY, Observable, firstValueFrom } from 'rxjs';
import {
  DocLineRole,
  DocLineUpdatedDto,
  SupplierPurchaseOrderDto,
  SupplierPurchaseOrderService,
} from '@digiwall/goliath-documents/proxy';
import { BaseThirdPartyDocLineEditor, LinesEditorComponent } from '../../../../components';
import { VatSystem, VatType } from '@digiwall/goliath-core/proxy';
import { ObserverQueue } from '@digiwall/core';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { DocType } from '../../../base-docs/detail/doc-type';
import { FieldTypes, IHtmlCellRendererParams } from '@digiwall/ag-grid';
import { ColorizedIcon, ColorizedIconHelper } from '../../../model/colorized-icon.model';

@Component({
  selector: 'document-supplier-purchase-order-lines-grid',
  templateUrl: './supplier-purchase-order-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class SupplierPurchaseOrderLinesEditorComponent extends BaseThirdPartyDocLineEditor<SupplierPurchaseOrderDto> {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  actionBtns: any[] = [];

  protected override handlingLinesUpdatedEvent = true;

  @Input() set supplierPurchaseOrder(value) {
    this._document = value;
    this.setLanguageCode();

    this.actionBtns = [];
    if (this._document?.isPartiallyReceived) {
      this.actionBtns.push({
        label: this.localizationService.instant('Doc::SupplierPurchaseOrder:CancelQty'),
        action: () => {
          this.cancelledQuantity();
        },
      });
    }

    this.actionBtns.push({
      label: this.localizationService.instant(
        'Doc::SupplierPurchaseOrder:UnlinkCustomerOrderLines',
      ),
      action: () => {
        this.unlinkCustomerOrderLines();
      },
    });
  }

  @Input() set readonly(value) {
    this._readonly = value;
  }

  readonly thirdPartyVatSystem = input<VatSystem>(undefined);

  public entityUpdateRequested = output<void>();

  protected articleLineRole: DocLineRole = DocLineRole.SupplierPurchaseOrderArticle;
  protected textLineRole: DocLineRole = DocLineRole.DocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;
  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticlePurchasableSelectionLookup(term);

  public columnDefs: Array<ColDef> = [];

  constructor(
    localizationService: LocalizationService,
    private supplierPurchaseOrderService: SupplierPurchaseOrderService,
    injector: Injector,
    updateQueue: ObserverQueue,
    protected confirmationService: ConfirmationService,
    protected toastService: ToasterService,
  ) {
    super(localizationService, supplierPurchaseOrderService, injector, updateQueue);

    this.initColDefs();
  }

  protected override colDef_StockStatusIcons: ColDef = {
    colId: 'stockStatusIcons',
    pinned: 'left',
    width: 60,
    type: FieldTypes.Html,
    cellRendererParams: {
      src: (p: ICellRendererParams) => {
        if (p.data != null) {
          let htmlClasses: Array<ColorizedIcon> = [];
          if (p.data.totalReceivedQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-truck1', color: 'green' });
          }
          if (p.data.backorderQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-triangle', color: 'orange' });
          }
          if (p.data.cancelledQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-impossible', color: 'red' });
          }
          if (p.data.hasOrderLineLink) {
            htmlClasses.push({ icon: 'digi-out-user-client', color: 'darkred' });
          }
          return ColorizedIconHelper.toHtml(htmlClasses);
        }
        return '';
      },
    } as IHtmlCellRendererParams,
  };

  private initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.splice(
      this.columnDefs.findIndex(colDef => colDef.colId === BaseThirdPartyDocLineEditor.CODE_COL_ID),
      0,
      this.colDef_StockStatusIcons,
    );
    this.columnDefs.push(
      ...([
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Purchase),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Purchase),
        this.colDef_TotalReceivedQuantity,
        this.colDef_RemainingQuantityToReceive,
        this.coldef_CancelledQuantity,
        ...(this.stockFeature.isEnabled
          ? [
              this.getColDef_TotalReceivedQuantityStock(),
              this.getColDef_RemainingQuantityToReceiveStock(),
              this.getColDef_CancelledQuantityStock(),
            ]
          : []),
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
      ] as Array<ColDef>),
    );
  }

  isChapter = line => line.lineRole == DocLineRole.DocChapter;

  public override isCellEditable(params: EditableCallbackParams<any, any>) {
    let isEditable = true;

    if (params.data.lineRole == this.articleLineRole && params.colDef.field != 'code') {
      isEditable = params.data.articleId != null;
    }
    switch (params.colDef.field) {
      case 'remainingQuantityToReceived':
      case 'totalReceivedQuantity':
      case 'remainingQuantityToReceivedInStockUnit':
      case 'totalReceivedQuantityInStockUnit':
        isEditable = false;
        break;
    }

    return (
      isEditable &&
      !params.data.hasOrderLineLink &&
      super.isCellEditable(params) &&
      !this._document.isPartiallyReceived
    );
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this._readonly) return [];

    return this.contextMenuItems(params);
  };

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this.supplierPurchaseOrderService.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.supplierPurchaseOrderService.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.supplierPurchaseOrderService.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
          );
          break;
        case 'unitWeight':
          observable = this.supplierPurchaseOrderService.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          let oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({
            update: [oldData],
          });
        },
      });
  };

  protected async cancelledQuantity() {
    const ids = this.selectedLines()
      .filter(line => line.lineRole == DocLineRole.SupplierPurchaseOrderArticle)
      .map(line => line.id);
    if (ids == null) {
      return;
    }
    if (await this.wantToCancelQuantityForDelivery()) {
      this._updateQueue
        .enqueue(() => this.supplierPurchaseOrderService.cancelLinesOrder(this._document.id, ids))
        .subscribe(docWithLinesUpdated => {
          this.toastService.success('Doc::CancelQuantityToReceiveSuccessfully');
          this._updateLines(docWithLinesUpdated);
        });
    }
  }

  private async wantToCancelQuantityForDelivery() {
    let status = await firstValueFrom(
      this.confirmationService.warn('Doc::WantToCancelQuantityForOrder', '::AreYouSure'),
    );
    return status == Confirmation.Status.confirm;
  }

  private unlinkCustomerOrderLines() {
    this.supplierPurchaseOrderService
      .unlinkCustomerOrderLines(this.selectedLines().map(line => line.id))
      .subscribe(deletedLinkCount => {
        this.toastService.success(
          this.localizationService.instant(
            'Doc::SupplierPurchaseOrder:UnlinkCustomerOrderLinesSuccessfully',
            deletedLinkCount.toString(),
          ),
        );
        this.lineGrid.grid.grid.api.deselectAll();
        this.entityUpdateRequested.emit();
      });
  }
}
